"use client";

import { useCallback, useEffect, useState } from "react";
import {
  getTextareaOverlayTextStyle,
  getTextareaRelativeOffset,
  type TextareaOverlayTextStyle,
} from "./analysis-target-textarea-layout";

export type TextareaOverlayMetrics = {
  height: number;
  top: number;
  scrollTop: number;
  scrollLeft: number;
  textStyle: TextareaOverlayTextStyle;
};

const EMPTY_METRICS: TextareaOverlayMetrics = {
  height: 0,
  top: 0,
  scrollTop: 0,
  scrollLeft: 0,
  textStyle: {},
};

const overlayTextStyleKeys = [
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "letterSpacing",
  "lineHeight",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "paddingTop",
  "textAlign",
  "textIndent",
  "textTransform",
  "wordSpacing",
] as const satisfies readonly (keyof TextareaOverlayTextStyle)[];

function hasSameTextStyle(
  previous: TextareaOverlayTextStyle,
  next: TextareaOverlayTextStyle,
): boolean {
  return overlayTextStyleKeys.every((key) => previous[key] === next[key]);
}

/**
 * textarea 本体と、相対座標の基準になりうる親レイアウト要素をまとめて返す。
 *
 * block-start addon の増減で input-group 全体の内部レイアウトが変わるため、
 * textarea 自身だけでなく input-group も監視対象に含める。
 */
function getOverlayObservationTargets(
  textarea: HTMLTextAreaElement,
): HTMLElement[] {
  const candidates = [
    textarea,
    textarea.closest('[data-slot="input-group"]'),
    textarea.parentElement,
  ];

  return [...new Set(candidates)].filter(
    (candidate): candidate is HTMLElement => candidate instanceof HTMLElement,
  );
}

/**
 * textarea に重ねる表示専用オーバーレイの配置メトリクスと文字スタイルを
 * 実 textarea と同期して返す共有 Hook。
 *
 * メンション装飾と音声入力の仮テキストなど、複数のオーバーレイが同じ計測ロジックを
 * 使うため切り出している。`syncKey` が変化したときは、addon の増減など DOM レイアウトの
 * 変化に追随するため強制的に再計測する。`useEffect` は ResizeObserver / scroll など
 * 外部イベントの購読という同期用途に限定している。
 */
export function useTextareaOverlayMetrics(
  textarea: HTMLTextAreaElement | null,
  syncKey?: string,
): TextareaOverlayMetrics {
  const [metrics, setMetrics] = useState<TextareaOverlayMetrics>(EMPTY_METRICS);

  const syncMetrics = useCallback(() => {
    if (!textarea) {
      setMetrics(EMPTY_METRICS);
      return;
    }

    const offset = getTextareaRelativeOffset(textarea);
    const textStyle = getTextareaOverlayTextStyle(textarea);
    setMetrics((previous) => {
      const next = {
        height: textarea.offsetHeight,
        top: offset.top,
        scrollTop: textarea.scrollTop,
        scrollLeft: textarea.scrollLeft,
        textStyle,
      };
      if (
        previous.height === next.height &&
        previous.top === next.top &&
        previous.scrollTop === next.scrollTop &&
        previous.scrollLeft === next.scrollLeft &&
        hasSameTextStyle(previous.textStyle, next.textStyle)
      ) {
        return previous;
      }
      return next;
    });
  }, [textarea]);

  useEffect(() => {
    if (!textarea) {
      syncMetrics();
      return;
    }

    let frameId: number | null = null;

    const requestSync = () => {
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        syncMetrics();
      });
    };

    requestSync();

    const observer = new ResizeObserver(requestSync);
    getOverlayObservationTargets(textarea).forEach((target) => {
      observer.observe(target);
    });
    window.addEventListener("resize", requestSync);
    textarea.addEventListener("scroll", requestSync, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", requestSync);
      textarea.removeEventListener("scroll", requestSync);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [syncMetrics, textarea]);

  useEffect(() => {
    if (syncKey !== undefined) {
      syncMetrics();
    }
  }, [syncKey, syncMetrics]);

  return metrics;
}
